import { Product } from '../types/content'

/**
 * One shot per product, all from the same set-up: a single unit on a white marble
 * slab against bone, soft daylight from the upper left. Every label carries the
 * monogram, REPUBLIC, the product name and the strength.
 */
const IMG: Record<string, string> = {
  '5-amino-1mq': 'https://cdn.magicpatterns.com/patterns/generated-images/0465b8e0-43b5-4ea5-93ce-b4ecee275355.jpg',
  'bpc-157': 'https://cdn.magicpatterns.com/patterns/generated-images/0849f073-75b7-46bd-862c-41ae59e89a68.jpg',
  'cjc-1295': 'https://cdn.magicpatterns.com/patterns/generated-images/bf0ba158-e11e-4e84-a759-9debd2be1121.jpg',
  'cjc-ipamorelin-blend': 'https://cdn.magicpatterns.com/patterns/generated-images/1bd76f17-1053-4778-9c42-4395539a13be.jpg',
  'ghk-cu': 'https://cdn.magicpatterns.com/patterns/generated-images/fffef08f-a445-491e-a48c-75d3b63e8134.jpg',
  'glow-stack': 'https://cdn.magicpatterns.com/patterns/generated-images/c815c2d2-9889-44d7-9641-6b7ccaceb769.jpg',
  glutathione: 'https://cdn.magicpatterns.com/patterns/generated-images/e2c7549a-0e13-4457-980f-c10525d5c491.jpg',
  'igf-1-lr3': 'https://cdn.magicpatterns.com/patterns/generated-images/ae9a3308-e511-408b-8ab3-c5a4d64dbb59.jpg',
  ipamorelin: 'https://cdn.magicpatterns.com/patterns/generated-images/57025505-4a4d-4dc2-a649-b3b1aee2aa49.jpg',
  'klow-stack-kpv': 'https://cdn.magicpatterns.com/patterns/generated-images/851d7c0f-1e39-44bc-adb4-35210dee8c31.jpg',
  kpv: 'https://cdn.magicpatterns.com/patterns/generated-images/15ec0068-c030-402c-88bc-572ac471bb91.jpg',
  'mots-c': 'https://cdn.magicpatterns.com/patterns/generated-images/dc80eb01-0e4a-4646-93cd-85f84d2fdfeb.jpg',
  'nad-plus': 'https://cdn.magicpatterns.com/patterns/generated-images/21aa67c5-b2f7-45e2-aba8-cc83da56108a.jpg',
  selank: 'https://cdn.magicpatterns.com/patterns/generated-images/33bb5f27-21d5-4ed2-9570-3a3be5ac8c9f.jpg',
  semaglutide: 'https://cdn.magicpatterns.com/patterns/generated-images/e383fca0-e200-4ce7-8ca4-432b32460c6a.jpg',
  semax: 'https://cdn.magicpatterns.com/patterns/generated-images/3175282d-8ffa-4623-bc86-92de1922fd9a.jpg',
  sermorelin: 'https://cdn.magicpatterns.com/patterns/generated-images/650ebe9b-5ffe-4d7e-8ae8-2735b56008e1.jpg',
  'slu-pp-332': 'https://cdn.magicpatterns.com/patterns/generated-images/a91fbba8-a512-4a39-8904-3ba2169a4be1.jpg',
  'ss-31': 'https://cdn.magicpatterns.com/patterns/generated-images/5c7269f2-eb3a-411d-8f98-0bc93e5566f7.jpg',
  'tb-500': 'https://cdn.magicpatterns.com/patterns/generated-images/14cfd181-7d14-4eff-b35d-162661d5cebe.jpg',
  tesamorelin: 'https://cdn.magicpatterns.com/patterns/generated-images/73ab8447-2871-47a1-9167-c9a7edc0a3c9.jpg',
  tirzepatide: 'https://cdn.magicpatterns.com/patterns/generated-images/cf31bd2f-57af-4526-8e20-30a1deb2bec2.jpg',
  'wolverine-blend': 'https://cdn.magicpatterns.com/patterns/generated-images/4361591c-087b-4dfe-9a22-93de258488b1.jpg',
}

const COMPOUNDED = 'Compounded · 503A pharmacy'
const OUTSOURCING = 'Compounded · 503B facility'
const FDA = 'FDA-approved'

/** Alphabetized. The shop groups by category and keeps this order within each group. */
export const products: Product[] = [
  {
    slug: '5-amino-1mq',
    name: '5-Amino-1MQ',
    category: 'weight-metabolic',
    status: COMPOUNDED,
    form: 'Oral capsule',
    strength: '50mg',
    route: 'Oral, daily',
    price: 139,
    cadence: '/mo',
    priceNote: '30 capsules',
    image: IMG['5-amino-1mq'],
    tagline: 'For people who are never going to inject anything.',
    description:
      'A small molecule that inhibits NNMT, an enzyme linked to fat cell metabolism. Used where a patient wants metabolic support but will not tolerate an injectable, and often paired with resistance training.',
    highlights: [
      'Oral daily capsule, no injections',
      'Often stacked with MOTS-C or a GLP-1',
      'Metabolic panel before and at week 12',
      'Cycled 12 weeks on, 4 weeks off',
    ],
    facts: [
      { label: 'Route', value: 'Oral capsule, once daily' },
      { label: 'Storage', value: 'Room temperature' },
      { label: 'Typical cycle', value: '12 weeks' },
      { label: 'Requires', value: 'Metabolic panel' },
    ],
    pairsWith: ['mots-c', 'semaglutide'],
  },
  {
    slug: 'bpc-157',
    name: 'BPC-157',
    category: 'recovery-repair',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '5mg',
    route: 'Subcutaneous or oral, daily',
    price: 149,
    cadence: '/mo',
    priceNote: 'Includes bacteriostatic water and syringes',
    image: IMG['bpc-157'],
    tagline: 'The recovery peptide most protocols are built around.',
    description:
      'A pentadecapeptide studied for soft-tissue and gut lining repair. Republic dispenses only US-compounded material with lot documentation — never overseas-sourced vials sold as research chemicals.',
    highlights: [
      'Injectable or oral capsule formulations',
      'Defined 8-week cycle with washout',
      'Lot and beyond-use date on every vial',
      'Commonly paired with TB-500',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous or oral' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '8 weeks' },
      { label: 'Requires', value: 'Clinician review' },
    ],
    pairsWith: ['tb-500', 'wolverine-blend'],
    popular: true,
  },
  {
    slug: 'cjc-1295',
    name: 'CJC-1295',
    category: 'growth-hormone',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '2mg',
    route: 'Subcutaneous, nightly',
    price: 159,
    cadence: '/mo',
    priceNote: 'Reconstitution supplies included',
    image: IMG['cjc-1295'],
    tagline: 'A GHRH analogue that extends your own GH pulse.',
    description:
      'Lengthens the natural growth hormone pulse rather than replacing the hormone. Most often prescribed alongside Ipamorelin, which is why the blend is the more common starting point.',
    highlights: [
      'Works with your natural nightly rhythm',
      'Usually combined with Ipamorelin',
      'IGF-1 checked before and during the cycle',
      'Nightly subcutaneous dosing',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, nightly' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '12 weeks' },
      { label: 'Requires', value: 'IGF-1 and hormone panel' },
    ],
    pairsWith: ['ipamorelin', 'cjc-ipamorelin-blend'],
  },
  {
    slug: 'cjc-ipamorelin-blend',
    name: 'CJC/Ipamorelin Blend',
    category: 'growth-hormone',
    status: COMPOUNDED,
    form: 'Blended vial',
    strength: '2mg / 2mg',
    route: 'Subcutaneous, nightly',
    price: 189,
    cadence: '/mo',
    priceNote: 'One vial instead of two',
    image: IMG['cjc-ipamorelin-blend'],
    tagline: 'Where most people start, and for good reason.',
    description:
      'A single compounded vial combining a GHRH analogue with a selective secretagogue. Together they produce a cleaner, more physiological release than either alone, with one injection instead of two.',
    highlights: [
      'One nightly injection, two mechanisms',
      'Lower cost than dosing separately',
      'IGF-1 monitored at baseline and week 12',
      'Cycled with a defined washout',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, nightly' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '12 weeks on, 4 off' },
      { label: 'Requires', value: 'IGF-1 and hormone panel' },
    ],
    pairsWith: ['tesamorelin', 'sermorelin'],
    popular: true,
  },
  {
    slug: 'ghk-cu',
    name: 'GHK-Cu',
    category: 'skin-hair',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '50mg',
    route: 'Subcutaneous, nightly',
    price: 129,
    cadence: '/mo',
    priceNote: 'Supplies included',
    image: IMG['ghk-cu'],
    tagline: 'The copper peptide behind collagen and skin quality.',
    description:
      'A naturally occurring copper tripeptide used for collagen synthesis, skin firmness and scar remodelling. The solution is a deep blue — that is the copper, and it is how you know the vial is what it says it is.',
    highlights: [
      'The blue one — that colour is the copper',
      'Photo-based review at month 3',
      'Foundation of the GLOW and KLOW stacks',
      'Well tolerated alongside retinoids',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, nightly' },
      { label: 'Storage', value: 'Room temperature, protect from light' },
      { label: 'Typical cycle', value: 'Continuous' },
      { label: 'Requires', value: 'Photo assessment' },
    ],
    pairsWith: ['glow-stack', 'klow-stack-kpv'],
  },
  {
    slug: 'glow-stack',
    name: 'GLOW Stack',
    category: 'skin-hair',
    status: COMPOUNDED,
    form: 'Blended vial',
    strength: 'GHK-Cu / BPC-157 / TB-500',
    route: 'Subcutaneous, daily',
    price: 199,
    cadence: '/mo',
    priceNote: 'Three peptides in one vial',
    image: IMG['glow-stack'],
    tagline: 'Skin, hair and tissue repair in a single daily dose.',
    description:
      'A compounded blend combining the copper peptide with the two most-used repair peptides. Written for patients who want appearance and recovery benefits without managing three separate vials.',
    highlights: [
      'Three peptides, one daily injection',
      'Cheaper than dosing each separately',
      'Photo review at month 3 and 6',
      'Commonly run for 12 weeks',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, daily' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '12 weeks' },
      { label: 'Requires', value: 'Clinician review' },
    ],
    pairsWith: ['klow-stack-kpv', 'ghk-cu'],
    popular: true,
  },
  {
    slug: 'glutathione',
    name: 'Glutathione',
    category: 'longevity-cellular',
    status: OUTSOURCING,
    form: 'Multi-dose vial',
    strength: '200mg/mL',
    route: 'Subcutaneous, 2–3× weekly',
    price: 89,
    cadence: '/mo',
    priceNote: 'Supplies and sharps return included',
    image: IMG.glutathione,
    tagline: 'The body’s primary antioxidant, without the infusion visit.',
    description:
      'Compounded glutathione for at-home subcutaneous dosing. Frequently run alongside NAD+ for patients working on oxidative stress, liver support and skin clarity.',
    highlights: [
      'At-home dosing, no infusion clinic fee',
      'Pairs with NAD+ on alternating days',
      'Supplies and sharps return included',
      'Reviewed every 12 weeks',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, 2–3× weekly' },
      { label: 'Storage', value: 'Refrigerated 2–8°C' },
      { label: 'Typical cycle', value: 'Continuous' },
      { label: 'Requires', value: 'Clinician review' },
    ],
    pairsWith: ['nad-plus', 'ss-31'],
  },
  {
    slug: 'igf-1-lr3',
    name: 'IGF-1 LR3',
    category: 'longevity-cellular',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '1mg',
    route: 'Subcutaneous, daily',
    price: 249,
    cadence: '/mo',
    priceNote: 'Advanced protocol · additional screening',
    image: IMG['igf-1-lr3'],
    tagline: 'Powerful, and not for everyone.',
    description:
      'A long-acting analogue of insulin-like growth factor 1. Prescribed selectively, with cancer screening history reviewed and IGF-1 levels tracked throughout — many patients are better served by a secretagogue.',
    highlights: [
      'Requires expanded screening before approval',
      'IGF-1 measured at baseline, week 4 and week 12',
      'Short, defined cycles only',
      'Not prescribed alongside high-dose GH secretagogues',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, daily' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '4–6 weeks' },
      { label: 'Requires', value: 'Extended panel and screening' },
    ],
    pairsWith: ['cjc-ipamorelin-blend', 'tesamorelin'],
  },
  {
    slug: 'ipamorelin',
    name: 'Ipamorelin',
    category: 'growth-hormone',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '2mg',
    route: 'Subcutaneous, nightly',
    price: 149,
    cadence: '/mo',
    priceNote: 'Reconstitution supplies included',
    image: IMG.ipamorelin,
    tagline: 'The most selective secretagogue, and the gentlest.',
    description:
      'Stimulates growth hormone release without meaningfully affecting cortisol or appetite, which is why it is the usual first choice for patients new to this category.',
    highlights: [
      'Minimal effect on cortisol or hunger',
      'Good first protocol in this category',
      'Nightly dosing supports sleep quality',
      'Often blended with CJC-1295',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, nightly' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '12 weeks' },
      { label: 'Requires', value: 'IGF-1 and hormone panel' },
    ],
    pairsWith: ['cjc-1295', 'cjc-ipamorelin-blend'],
  },
  {
    slug: 'klow-stack-kpv',
    name: 'KLOW Stack with KPV',
    category: 'skin-hair',
    status: COMPOUNDED,
    form: 'Blended vial',
    strength: 'GHK-Cu / BPC-157 / TB-500 / KPV',
    route: 'Subcutaneous, daily',
    price: 239,
    cadence: '/mo',
    priceNote: 'Four peptides in one vial',
    image: IMG['klow-stack-kpv'],
    tagline: 'The GLOW stack plus an anti-inflammatory peptide.',
    description:
      'Adds KPV to the GLOW blend for patients whose skin or gut symptoms are inflammation-driven. One daily injection covering repair, appearance and inflammatory signalling.',
    highlights: [
      'Four peptides, one daily injection',
      'Chosen when inflammation drives symptoms',
      'Photo and symptom review at month 3',
      'Commonly run for 12 weeks',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, daily' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '12 weeks' },
      { label: 'Requires', value: 'Clinician review' },
    ],
    pairsWith: ['glow-stack', 'kpv'],
  },
  {
    slug: 'kpv',
    name: 'KPV',
    category: 'recovery-repair',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '500mcg',
    route: 'Subcutaneous, daily',
    price: 159,
    cadence: '/mo',
    priceNote: 'Supplies included',
    image: IMG.kpv,
    tagline: 'When inflammation is the thing holding you back.',
    description:
      'A tripeptide fragment of alpha-MSH used where inflammation is the limiting factor — gut, skin or joint. Often the quiet addition that makes a recovery stack finally work.',
    highlights: [
      'Daily subcutaneous dosing',
      'Frequently added to repair stacks',
      'Well tolerated at standard dosing',
      'Reviewed at week 8',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, daily' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '8 weeks' },
      { label: 'Requires', value: 'Clinician review' },
    ],
    pairsWith: ['bpc-157', 'klow-stack-kpv'],
  },
  {
    slug: 'mots-c',
    name: 'MOTS-C',
    category: 'weight-metabolic',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '10mg',
    route: 'Subcutaneous, 2–3× weekly',
    price: 189,
    cadence: '/mo',
    priceNote: 'Supplies included',
    image: IMG['mots-c'],
    tagline: 'Keeps your energy up while the weight comes down.',
    description:
      'Encoded in mitochondrial DNA and studied for insulin sensitivity, exercise capacity and metabolic flexibility. Often used alongside a GLP-1 to support energy during weight reduction.',
    highlights: [
      'Two to three doses per week',
      'Supports energy during a calorie deficit',
      'A1c and fasting insulin tracked',
      'Cycled 8–12 weeks',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, 2–3× weekly' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '8–12 weeks' },
      { label: 'Requires', value: 'Metabolic panel' },
    ],
    pairsWith: ['5-amino-1mq', 'tirzepatide'],
  },
  {
    slug: 'nad-plus',
    name: 'NAD+',
    category: 'longevity-cellular',
    status: OUTSOURCING,
    form: 'Multi-dose vial',
    strength: '100mg/mL',
    route: 'Subcutaneous, weekly',
    price: 139,
    cadence: '/mo',
    priceNote: 'Weekly dosing · supplies included',
    image: IMG['nad-plus'],
    tagline: 'Cellular energy support without the infusion-clinic markup.',
    description:
      'Compounded NAD+ for weekly subcutaneous dosing at a fraction of an infusion visit for the same monthly quantity, with titration guidance for injection-site tolerance.',
    highlights: [
      'Weekly at-home subcutaneous dosing',
      'No clinic visit or infusion fee',
      'Titration guidance included',
      'Pairs with glutathione',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, weekly' },
      { label: 'Storage', value: 'Refrigerated 2–8°C' },
      { label: 'Typical cycle', value: 'Continuous' },
      { label: 'Requires', value: 'Clinician review' },
    ],
    pairsWith: ['glutathione', 'ss-31'],
    popular: true,
  },
  {
    slug: 'selank',
    name: 'Selank',
    category: 'cognitive-mood',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '5mg',
    route: 'Subcutaneous, daily',
    price: 119,
    cadence: '/mo',
    priceNote: 'Supplies included',
    image: IMG.selank,
    tagline: 'Calmer stress response, without sedation.',
    description:
      'An anxiolytic peptide studied for stress resilience and anxiety without the sedation or dependence profile of conventional options. Dosed daily in short courses.',
    highlights: [
      'Does not sedate or dull you',
      'Daily subcutaneous dosing',
      'Often alternated with Semax',
      'Short courses with review at week 8',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, daily' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '8 weeks' },
      { label: 'Requires', value: 'Clinician review' },
    ],
    pairsWith: ['semax', 'nad-plus'],
  },
  {
    slug: 'semaglutide',
    name: 'Semaglutide',
    category: 'weight-metabolic',
    status: FDA,
    form: 'Single-dose injector pen',
    strength: '0.25mg – 2.4mg weekly',
    route: 'Subcutaneous, weekly',
    price: 249,
    cadence: '/mo',
    priceNote: '4 weekly doses · dose changes included',
    image: IMG.semaglutide,
    tagline: 'The most studied GLP-1 there is.',
    description:
      'A GLP-1 receptor agonist for chronic weight management. Your clinician sets the escalation pace against tolerability and lab work, and the monthly figure shown is the monthly figure you are charged.',
    highlights: [
      'Extensive cardiovascular outcome data',
      'Weekly dosing with a fixed-dose pen',
      'Nutrition and resistance-training guidance included',
      'Side-effect support within one business day',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, weekly' },
      { label: 'Storage', value: 'Refrigerated 2–8°C' },
      { label: 'Typical cycle', value: 'Ongoing with maintenance plan' },
      { label: 'Requires', value: 'Metabolic panel' },
    ],
    pairsWith: ['mots-c', 'tirzepatide'],
    popular: true,
    faq: [
      {
        question: 'What does Semaglutide actually do?',
        answer:
          'It is a GLP-1. It blunts hunger and makes you feel full sooner. In trials, people lost around 15% of their body weight alongside diet and exercise changes.',
      },
      {
        question: 'Semaglutide or Tirzepatide?',
        answer:
          'Tirzepatide produced bigger average losses in trials. Semaglutide has been around longer and has more real-world data behind it. If you want a proven option at the lower price, start here.',
      },
      {
        question: 'What about the nausea?',
        answer:
          'Side effects are the number one reason people quit. Your doctor can split you onto twice-weekly dosing, which smooths out the spike that usually comes with a single weekly shot.',
      },
      {
        question: 'What am I paying in total?',
        answer:
          '$249 a month for the medication, plus $39 a month for membership. That covers your doctor, your bloodwork and every dose change. It is the same number at 0.25mg as it is at 2.4mg.',
      },
    ],
  },
  {
    slug: 'semax',
    name: 'Semax',
    category: 'cognitive-mood',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '5mg',
    route: 'Subcutaneous, daily',
    price: 119,
    cadence: '/mo',
    priceNote: 'Supplies included',
    image: IMG.semax,
    tagline: 'For the days you cannot hold a thought.',
    description:
      'A nootropic peptide studied for attention, memory and recovery from cognitive fatigue. Usually run in short courses and often alternated with Selank.',
    highlights: [
      'Dosed in the morning',
      'Short courses, not continuous',
      'Often alternated with Selank',
      'Reviewed at week 8',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, daily' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '8 weeks' },
      { label: 'Requires', value: 'Clinician review' },
    ],
    pairsWith: ['selank', 'nad-plus'],
  },
  {
    slug: 'sermorelin',
    name: 'Sermorelin',
    category: 'growth-hormone',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '9mg',
    route: 'Subcutaneous, nightly',
    price: 129,
    cadence: '/mo',
    priceNote: 'Most accessible protocol in this category',
    image: IMG.sermorelin,
    tagline: 'The gentlest place to start in this category.',
    description:
      'A shorter-acting GHRH analogue with a long clinical history. A sensible starting protocol for patients who want to see how they respond before moving to a blend.',
    highlights: [
      'Lowest-cost protocol in this category',
      'Short half-life, easy to stop',
      'Nightly dosing supports sleep',
      'IGF-1 checked at week 12',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, nightly' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '12 weeks' },
      { label: 'Requires', value: 'IGF-1 and hormone panel' },
    ],
    pairsWith: ['ipamorelin', 'cjc-ipamorelin-blend'],
  },
  {
    slug: 'slu-pp-332',
    name: 'SLU-PP-332',
    category: 'weight-metabolic',
    status: COMPOUNDED,
    form: 'Oral capsule',
    strength: '500mcg',
    route: 'Oral, daily',
    price: 169,
    cadence: '/mo',
    priceNote: '30 capsules',
    image: IMG['slu-pp-332'],
    tagline: 'The one people call exercise in a capsule.',
    description:
      'An ERR agonist studied for endurance and fat oxidation — sometimes described as an exercise mimetic. Newer than most of the catalogue, so it is prescribed conservatively with tighter follow-up.',
    highlights: [
      'Oral daily capsule',
      'Shorter cycles with closer monitoring',
      'Reviewed at week 6 and week 12',
      'Not a substitute for training',
    ],
    facts: [
      { label: 'Route', value: 'Oral capsule, once daily' },
      { label: 'Storage', value: 'Room temperature' },
      { label: 'Typical cycle', value: '8 weeks' },
      { label: 'Requires', value: 'Metabolic panel' },
    ],
    pairsWith: ['5-amino-1mq', 'mots-c'],
  },
  {
    slug: 'ss-31',
    name: 'SS-31',
    category: 'longevity-cellular',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '10mg',
    route: 'Subcutaneous, daily',
    price: 229,
    cadence: '/mo',
    priceNote: 'Supplies included',
    image: IMG['ss-31'],
    tagline: 'Targets the mitochondrial membrane directly.',
    description:
      'Also known as elamipretide, SS-31 concentrates in the inner mitochondrial membrane and is studied for cellular energy production and oxidative stress in fatigue-driven presentations.',
    highlights: [
      'Daily subcutaneous dosing',
      'Used in fatigue and recovery protocols',
      'Frequently combined with NAD+',
      'Reviewed at week 12',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, daily' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '12 weeks' },
      { label: 'Requires', value: 'Extended longevity panel' },
    ],
    pairsWith: ['nad-plus', 'glutathione'],
  },
  {
    slug: 'tb-500',
    name: 'TB-500',
    category: 'recovery-repair',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '5mg',
    route: 'Subcutaneous, 2× weekly',
    price: 179,
    cadence: '/mo',
    priceNote: 'Supplies included',
    image: IMG['tb-500'],
    tagline: 'Systemic repair, twice a week.',
    description:
      'A synthetic fragment of thymosin beta-4 used for flexibility, systemic tissue repair and recovery from overuse injuries. The standard partner to BPC-157.',
    highlights: [
      'Twice-weekly dosing',
      'Standard partner to BPC-157',
      'Loading phase then maintenance',
      'Available blended as Wolverine',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, 2× weekly' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '8 weeks' },
      { label: 'Requires', value: 'Clinician review' },
    ],
    pairsWith: ['bpc-157', 'wolverine-blend'],
  },
  {
    slug: 'tesamorelin',
    name: 'Tesamorelin',
    category: 'growth-hormone',
    status: COMPOUNDED,
    form: 'Lyophilised vial',
    strength: '10mg',
    route: 'Subcutaneous, nightly',
    price: 219,
    cadence: '/mo',
    priceNote: 'Includes bacteriostatic water and syringes',
    image: IMG.tesamorelin,
    tagline: 'The one with real data behind belly fat.',
    description:
      'A growth hormone releasing factor analogue with clinical evidence for reducing visceral adipose tissue. Dispensed with a written cycling and washout schedule and a follow-up panel before renewal.',
    highlights: [
      'Strongest evidence for visceral fat',
      'Nightly dosing, cycled 12 weeks on',
      'IGF-1 monitored before and during',
      'Reconstitution supplies included',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, nightly' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '12 weeks on, 4 off' },
      { label: 'Requires', value: 'IGF-1 and metabolic panel' },
    ],
    pairsWith: ['cjc-ipamorelin-blend', 'semaglutide'],
  },
  {
    slug: 'tirzepatide',
    name: 'Tirzepatide',
    category: 'weight-metabolic',
    status: FDA,
    form: 'Single-dose injector pen',
    strength: '2.5mg – 15mg weekly',
    route: 'Subcutaneous, weekly',
    price: 349,
    cadence: '/mo',
    priceNote: '4 weekly doses · dose changes included',
    image: IMG.tirzepatide,
    tagline: 'The strongest weight-loss results we have data for.',
    description:
      'A dual GIP and GLP-1 receptor agonist. Republic dispenses it at the published pharmacy rate plus a fixed handling fee, with the escalation schedule written by your clinician rather than applied automatically.',
    highlights: [
      'Average 18–22% total body weight reduction in trial data',
      'Weekly self-administered pen',
      'Cold-chain shipping included',
      'Titration reviewed at every dose step',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, weekly' },
      { label: 'Storage', value: 'Refrigerated 2–8°C' },
      { label: 'Typical cycle', value: 'Ongoing with maintenance plan' },
      { label: 'Requires', value: 'Metabolic panel' },
    ],
    pairsWith: ['semaglutide', 'mots-c'],
    popular: true,
    faq: [
      {
        question: 'What does Tirzepatide actually do?',
        answer:
          'It hits two receptors instead of one, GLP-1 and GIP, which affects appetite, metabolism and blood sugar together. Trial participants lost around 20–22% of their body weight.',
      },
      {
        question: 'Why is it more than Semaglutide?',
        answer:
          'Because it costs us more, and only slightly. Plenty of providers charge nearly double for Tirzepatide. We did not think that gap was defensible, so ours is $100.',
      },
      {
        question: 'What about the nausea?',
        answer:
          'Your doctor can put you on twice-weekly dosing instead of one larger weekly shot, which takes the edge off the peak most people struggle with.',
      },
      {
        question: 'Does the price change as my dose goes up?',
        answer:
          'No. $349 a month at 2.5mg, $349 a month at 15mg. Plus $39 for membership, and it is locked for a year.',
      },
    ],
  },
  {
    slug: 'wolverine-blend',
    name: 'Wolverine Blend',
    category: 'recovery-repair',
    status: COMPOUNDED,
    form: 'Blended vial',
    strength: 'BPC-157 5mg / TB-500 5mg',
    route: 'Subcutaneous, daily',
    price: 219,
    cadence: '/mo',
    priceNote: 'Both peptides in one vial',
    image: IMG['wolverine-blend'],
    tagline: 'Both repair peptides, one injection.',
    description:
      'The two most-used recovery peptides compounded into a single vial. Chosen by patients already running both, or by anyone who wants the full repair protocol without managing two reconstitutions.',
    highlights: [
      'One daily injection instead of two',
      'Lower cost than dosing separately',
      'Standard 8-week recovery cycle',
      'Lot documentation with every shipment',
    ],
    facts: [
      { label: 'Route', value: 'Subcutaneous, daily' },
      { label: 'Storage', value: 'Refrigerated after reconstitution' },
      { label: 'Typical cycle', value: '8 weeks' },
      { label: 'Requires', value: 'Clinician review' },
    ],
    pairsWith: ['bpc-157', 'tb-500'],
    popular: true,
  },
]

export function getProduct(slug: string): Product | undefined {
  return products.find((product) => product.slug === slug)
}

export function productsInCategory(categorySlug: string): Product[] {
  return products.filter((product) => product.category === categorySlug)
}
